import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useGetSessionsQuery,
  useGetStatistiquesParticipationQuery,
  AbsentPartielDetail,
} from '../../services/emploisTempsApi';
import {
  BarChart3, Users, UserX, UserCheck, TrendingUp, Printer,
  ChevronDown, ChevronUp, Search, GraduationCap, Building2, AlertTriangle, CheckCircle2,
  ExternalLink,
} from 'lucide-react';
import { Spinner } from '../../components/ui/Spinner';
import { Cycle, SelecteurCycle } from '../../components/ui/SelecteurCycle';

// ── Colors ──
const POLE_COLORS: Record<string, { bar: string; light: string; text: string }> = {
  'Polytechnique': { bar: 'bg-blue-500', light: 'bg-blue-50 dark:bg-blue-900/15 border-blue-200 dark:border-blue-800', text: 'text-blue-700 dark:text-blue-300' },
  'Commerce': { bar: 'bg-emerald-500', light: 'bg-emerald-50 dark:bg-emerald-900/15 border-emerald-200 dark:border-emerald-800', text: 'text-emerald-700 dark:text-emerald-300' },
  'Juridique': { bar: 'bg-amber-500', light: 'bg-amber-50 dark:bg-amber-900/15 border-amber-200 dark:border-amber-800', text: 'text-amber-700 dark:text-amber-300' },
};
const NIV_COLORS: Record<string, string> = { 'L1': 'bg-blue-500', 'L2': 'bg-indigo-500', 'L3': 'bg-purple-500', 'M1': 'bg-pink-500', 'M2': 'bg-rose-500' };

function getPC(pole: string) { return POLE_COLORS[pole] || { bar: 'bg-gray-400', light: 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700', text: 'text-gray-700 dark:text-gray-300' }; }

// ── Components ──
function TauxCircle({ taux, size = 80 }: { taux: number; size?: number }) {
  const r = (size - 8) / 2, circ = 2 * Math.PI * r, offset = circ - (taux / 100) * circ;
  const color = taux >= 80 ? '#16a34a' : taux >= 60 ? '#ca8a04' : '#dc2626';
  return (
    <svg width={size} height={size} className="shrink-0">
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="currentColor" strokeWidth={5} className="text-gray-200 dark:text-gray-700" />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={5}
        strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
        transform={`rotate(-90 ${size/2} ${size/2})`} className="transition-all duration-700" />
      <text x="50%" y="50%" textAnchor="middle" dy="0.35em" fontSize={size*0.22} fontWeight="bold" fill={color}>{taux}%</text>
    </svg>
  );
}

function StatutBadge({ statut, nbPassees, nbPrevues }: { statut: string; nbPassees: number; nbPrevues: number }) {
  if (statut === 'absent') return <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">Absent</span>;
  if (statut === 'partiel') return <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300">{nbPassees}/{nbPrevues} matières</span>;
  return <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">Complet</span>;
}

// ── Print builders ──
const FONT_OPTIONS = [
  { value: "'Segoe UI', Arial, sans-serif", label: 'Segoe UI (moderne)' },
  { value: "'Georgia', 'Times New Roman', serif", label: 'Georgia (classique)' },
  { value: "'Palatino Linotype', 'Book Antiqua', serif", label: 'Palatino (élégant)' },
  { value: "'Trebuchet MS', 'Lucida Sans', sans-serif", label: 'Trebuchet (aéré)' },
  { value: "'Cambria', 'Hoefler Text', serif", label: 'Cambria (institutionnel)' },
  { value: "'Calibri', 'Helvetica Neue', sans-serif", label: 'Calibri (professionnel)' },
];

function buildPrintHTML(data: any, fontFamily: string, includeAbsents = false) {
  const N = '#1A2E4A', G = '#C8A84E', GR = '#16a34a', RD = '#dc2626', OR = '#ca8a04';
  const today = new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
  const g = data.global;
  const th = `padding:8px 12px;border:1px solid ${N};background:${N};color:${G};font-weight:bold;text-transform:uppercase;font-size:9pt;`;

  const nRows = Object.entries(data.par_niveau as Record<string, any>).map(([niv, d]: [string, any]) =>
    `<tr><td style="padding:7px 12px;border:1px solid #ddd;font-weight:bold">${niv}</td>
     <td style="padding:7px 12px;border:1px solid #ddd;text-align:center">${d.inscrits}</td>
     <td style="padding:7px 12px;border:1px solid #ddd;text-align:center;color:${GR};font-weight:bold">${d.presents}</td>
     <td style="padding:7px 12px;border:1px solid #ddd;text-align:center;color:${GR}">${d.complets}</td>
     <td style="padding:7px 12px;border:1px solid #ddd;text-align:center;color:${OR}">${d.partiels}</td>
     <td style="padding:7px 12px;border:1px solid #ddd;text-align:center;color:${RD}">${d.absents}</td>
     <td style="padding:7px 12px;border:1px solid #ddd;text-align:center;font-weight:bold">${d.taux}%</td></tr>`
  ).join('');

  const pRows = Object.entries(data.par_pole as Record<string, any>).map(([pole, d]: [string, any]) =>
    `<tr><td style="padding:7px 12px;border:1px solid #ddd;font-weight:bold">${pole}</td>
     <td style="padding:7px 12px;border:1px solid #ddd;text-align:center">${d.inscrits}</td>
     <td style="padding:7px 12px;border:1px solid #ddd;text-align:center;color:${GR};font-weight:bold">${d.presents}</td>
     <td style="padding:7px 12px;border:1px solid #ddd;text-align:center;color:${RD}">${d.absents}</td>
     <td style="padding:7px 12px;border:1px solid #ddd;text-align:center;font-weight:bold">${d.taux}%</td></tr>`
  ).join('');

  return `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Participation - ${data.session.nom}</title>
<style>@page{size:A4;margin:12mm}*{margin:0;padding:0;box-sizing:border-box;-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important}
body{font-family:${fontFamily};font-size:10pt;color:#1a1a1a}table{width:100%;border-collapse:collapse}
tbody tr:nth-child(even) td{background:#f8fafc}</style></head>
<body style="padding:20px">
<table style="margin-bottom:10px"><tr>
  <td style="width:75px;vertical-align:top"><img src="/logo.png" style="width:65px;height:65px;object-fit:contain" onerror="this.style.display='none'"/></td>
  <td style="vertical-align:middle;text-align:center">
    <div style="font-size:14pt;font-weight:bold;color:${N};text-transform:uppercase;letter-spacing:1px">Institut International 2i</div>
    <div style="font-size:8pt;color:#666;font-style:italic">Excellence et Innovation</div>
    <div style="border-top:2px solid ${G};margin:5px auto;width:50%"></div>
  </td><td style="width:75px"></td>
</tr></table>
<div style="text-align:center;margin-bottom:15px">
  <div style="font-size:13pt;font-weight:bold;color:${N};text-transform:uppercase">Bilan de Participation aux Examens</div>
  <div style="font-size:10pt;color:#555;margin-top:3px">${data.session.nom}</div>
  <div style="font-size:9pt;color:#888">Année ${data.session.annee_academique}</div>
</div>
<div style="display:flex;gap:10px;margin-bottom:18px;justify-content:center">
  <div style="background:#eff6ff;border:1px solid #bfdbfe;border-radius:8px;padding:12px 20px;text-align:center">
    <div style="font-size:20pt;font-weight:bold;color:${N}">${g.total_inscrits}</div><div style="font-size:8pt;color:#666">INSCRITS</div></div>
  <div style="background:#f0fdf4;border:1px solid #bbf7d0;border-radius:8px;padding:12px 20px;text-align:center">
    <div style="font-size:20pt;font-weight:bold;color:${GR}">${g.total_presents}</div><div style="font-size:8pt;color:#666">PRÉSENTS</div></div>
  <div style="background:#fff7ed;border:1px solid #fed7aa;border-radius:8px;padding:12px 20px;text-align:center">
    <div style="font-size:20pt;font-weight:bold;color:${OR}">${g.total_partiels}</div><div style="font-size:8pt;color:#666">PARTIELS</div></div>
  <div style="background:#fef2f2;border:1px solid #fecaca;border-radius:8px;padding:12px 20px;text-align:center">
    <div style="font-size:20pt;font-weight:bold;color:${RD}">${g.total_absents}</div><div style="font-size:8pt;color:#666">ABSENTS</div></div>
  <div style="background:#fefce8;border:1px solid #fef08a;border-radius:8px;padding:12px 20px;text-align:center">
    <div style="font-size:20pt;font-weight:bold;color:#92400e">${g.taux_participation}%</div><div style="font-size:8pt;color:#666">TAUX</div></div>
</div>
<div style="font-weight:bold;font-size:11pt;color:${N};margin:14px 0 6px;border-bottom:2px solid ${G};padding-bottom:3px">Bilan par Niveau</div>
<table><thead><tr><th style="${th}text-align:left">Niveau</th><th style="${th}text-align:center">Inscrits</th><th style="${th}text-align:center">Présents</th><th style="${th}text-align:center">Complets</th><th style="${th}text-align:center">Partiels</th><th style="${th}text-align:center">Absents</th><th style="${th}text-align:center">Taux</th></tr></thead>
<tbody>${nRows}
<tr style="background:#f1f5f9;font-weight:bold"><td style="padding:8px 12px;border:1px solid #ddd">TOTAL</td>
<td style="padding:8px 12px;border:1px solid #ddd;text-align:center">${g.total_inscrits}</td>
<td style="padding:8px 12px;border:1px solid #ddd;text-align:center;color:${GR}">${g.total_presents}</td>
<td style="padding:8px 12px;border:1px solid #ddd;text-align:center">${g.total_complets}</td>
<td style="padding:8px 12px;border:1px solid #ddd;text-align:center;color:${OR}">${g.total_partiels}</td>
<td style="padding:8px 12px;border:1px solid #ddd;text-align:center;color:${RD}">${g.total_absents}</td>
<td style="padding:8px 12px;border:1px solid #ddd;text-align:center">${g.taux_participation}%</td></tr></tbody></table>
<div style="font-weight:bold;font-size:11pt;color:${N};margin:16px 0 6px;border-bottom:2px solid ${G};padding-bottom:3px">Bilan par Pôle</div>
<table><thead><tr><th style="${th}text-align:left">Pôle</th><th style="${th}text-align:center">Inscrits</th><th style="${th}text-align:center">Présents</th><th style="${th}text-align:center">Absents</th><th style="${th}text-align:center">Taux</th></tr></thead><tbody>${pRows}</tbody></table>

${includeAbsents ? (() => {
    const absRows = (data.absents_et_partiels || []).map((a: any, i: number) =>
      `<tr style="background:${i%2===0?'#fff':'#f8fafc'}">
        <td style="padding:4px 8px;border:1px solid #ddd;text-align:center;font-size:8pt">${i+1}</td>
        <td style="padding:4px 8px;border:1px solid #ddd;font-family:monospace;font-size:8pt">${a.matricule}</td>
        <td style="padding:4px 8px;border:1px solid #ddd;font-weight:bold">${a.nom} ${a.prenoms}</td>
        <td style="padding:4px 8px;border:1px solid #ddd;text-align:center">${a.niveau}</td>
        <td style="padding:4px 8px;border:1px solid #ddd">${a.classe}</td>
        <td style="padding:4px 8px;border:1px solid #ddd;text-align:center;font-weight:bold;color:${a.statut==='absent'?RD:OR}">${a.statut === 'absent' ? 'Absent' : a.nb_passees+'/'+a.nb_prevues}</td>
        <td style="padding:4px 8px;border:1px solid #ddd;font-size:8pt;color:${RD}">${a.matieres_manquees.join(', ')}</td>
      </tr>`).join('');
    return `<div style="page-break-before:always"></div>
<div style="font-weight:bold;font-size:11pt;color:${N};margin:16px 0 6px;border-bottom:2px solid ${RD};padding-bottom:3px">Liste des Absents & Présences Partielles (${data.absents_et_partiels.length})</div>
<table><thead><tr>
  <th style="${th}text-align:center;width:30px">N°</th><th style="${th}text-align:left">Matricule</th><th style="${th}text-align:left">Nom & Prénoms</th>
  <th style="${th}text-align:center">Niv.</th><th style="${th}text-align:left">Classe</th><th style="${th}text-align:center">Statut</th><th style="${th}text-align:left">Matière(s) manquée(s)</th>
</tr></thead><tbody>${absRows}</tbody></table>`;
  })() : ''}

<div style="margin-top:20px;text-align:center;font-size:8pt;color:#999;border-top:1px solid #ddd;padding-top:6px">
  Document généré le ${today} — Système de Gestion des Notes — Institut International 2i</div>
</body></html>`;
}

function buildAbsentsPrintHTML(data: any, list: AbsentPartielDetail[], filterLabel: string, fontFamily: string) {
  const N = '#1A2E4A', G = '#C8A84E';
  const today = new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
  const th = `padding:7px 10px;border:1px solid ${N};background:${N};color:${G};font-weight:bold;font-size:8.5pt;`;
  const rows = list.map((a, i) =>
    `<tr style="background:${i%2===0?'#fff':'#f8fafc'}">
      <td style="padding:4px 8px;border:1px solid #ddd;text-align:center;font-size:8pt">${i+1}</td>
      <td style="padding:4px 8px;border:1px solid #ddd;font-family:monospace;font-size:8pt">${a.matricule}</td>
      <td style="padding:4px 8px;border:1px solid #ddd;font-weight:bold;font-size:9pt">${a.nom} ${a.prenoms}</td>
      <td style="padding:4px 8px;border:1px solid #ddd;text-align:center">${a.niveau}</td>
      <td style="padding:4px 8px;border:1px solid #ddd">${a.classe}</td>
      <td style="padding:4px 8px;border:1px solid #ddd;text-align:center;font-weight:bold;color:${a.statut==='absent'?'#dc2626':'#ca8a04'}">${a.statut === 'absent' ? 'Absent' : `${a.nb_passees}/${a.nb_prevues}`}</td>
      <td style="padding:4px 8px;border:1px solid #ddd;font-size:8pt;color:#dc2626">${a.matieres_manquees.join(', ')}</td>
    </tr>`).join('');

  return `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Absents - ${data.session.nom}</title>
<style>@page{size:A4;margin:10mm}*{margin:0;padding:0;box-sizing:border-box;-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important}
body{font-family:${fontFamily};font-size:9pt;color:#1a1a1a}table{width:100%;border-collapse:collapse}
tbody tr:nth-child(even) td{background:#f8fafc}</style></head>
<body style="padding:15px">
<table style="margin-bottom:8px"><tr>
  <td style="width:65px;vertical-align:top"><img src="/logo.png" style="width:55px;height:55px;object-fit:contain" onerror="this.style.display='none'"/></td>
  <td style="vertical-align:middle;text-align:center">
    <div style="font-size:13pt;font-weight:bold;color:${N};text-transform:uppercase">Institut International 2i</div>
    <div style="font-size:7pt;color:#666">Excellence et Innovation</div>
    <div style="border-top:2px solid ${G};margin:4px auto;width:40%"></div>
  </td><td style="width:65px"></td></tr></table>
<div style="text-align:center;margin-bottom:12px">
  <div style="font-size:12pt;font-weight:bold;color:${N};text-transform:uppercase">Liste des Étudiants Absents / Présence Partielle</div>
  <div style="font-size:9pt;color:#555">${data.session.nom} | Filtre: ${filterLabel}</div>
  <div style="font-size:9pt;color:#888;margin-top:2px">Total: ${list.length} étudiant(s)</div>
</div>
<table><thead><tr>
  <th style="${th}text-align:center;width:30px">N°</th><th style="${th}text-align:left">Matricule</th><th style="${th}text-align:left">Nom & Prénoms</th>
  <th style="${th}text-align:center">Niv.</th><th style="${th}text-align:left">Classe</th><th style="${th}text-align:center">Statut</th><th style="${th}text-align:left">Matière(s) manquée(s)</th>
</tr></thead><tbody>${rows}</tbody></table>
<div style="margin-top:16px;text-align:center;font-size:7.5pt;color:#999;border-top:1px solid #ddd;padding-top:5px">
  Document généré le ${today} — Institut International 2i</div></body></html>`;
}

// ── Main ──
type DetailTab = 'absents' | 'partiels' | 'complets' | 'vrais_absents';

const StatistiquesParticipation: React.FC = () => {
  const navigate = useNavigate();
  const [selectedSessionId, setSelectedSessionId] = useState<number | null>(null);
  const [showList, setShowList] = useState(false);
  const [detailTab, setDetailTab] = useState<DetailTab>('absents');
  const [listFilter, setListFilter] = useState({ search: '', niveau: '', pole: '', statut: '' as '' | 'absent' | 'partiel', nbManquees: '' as '' | string, nbPassees: '' as '' | string });
  const [printFont, setPrintFont] = useState(FONT_OPTIONS[0].value);
  const [includeAbsentsInBilan, setIncludeAbsentsInBilan] = useState(false);

  const { data: sessions = [] } = useGetSessionsQuery({ annee_academique: '2025-2026' });
  // Licence et master ne se mélangent pas dans les statistiques (2026-10-02).
  const [cycle, setCycle] = useState<Cycle>('licence');
  const { data, isLoading, isFetching } = useGetStatistiquesParticipationQuery(
    { session_id: selectedSessionId!, cycle }, { skip: !selectedSessionId }
  );

  // Listes par type
  const filteredList = useMemo(() => {
    if (!data) return [];
    const source = detailTab === 'absents'
      ? ((data as any).absents_totaux || data.absents_et_partiels.filter(a => a.statut === 'absent'))
      : detailTab === 'partiels'
        ? ((data as any).absents_partiels || data.absents_et_partiels.filter(a => a.statut === 'partiel'))
        : detailTab === 'vrais_absents'
          ? ((data as any).vrais_absents || [])
          : data.absents_et_partiels;  // 'complets' handled below
    return source.filter((a: any) => {
      const q = listFilter.search.toLowerCase();
      if (q && !a.nom.toLowerCase().includes(q) && !a.prenoms.toLowerCase().includes(q) && !a.matricule.toLowerCase().includes(q)) return false;
      if (listFilter.niveau && a.niveau !== listFilter.niveau) return false;
      if (listFilter.pole && a.pole !== listFilter.pole) return false;
      if (listFilter.nbManquees && Number(listFilter.nbManquees) !== a.nb_manquees) return false;
      if (listFilter.nbPassees && Number(listFilter.nbPassees) !== a.nb_passees) return false;
      return true;
    });
  }, [data, listFilter, detailTab]);

  // Valeurs distinctes pour les filtres nb matières
  const nbManqueesOptions = useMemo(() => {
    if (!data) return [];
    const source = (data as any).absents_partiels || data.absents_et_partiels.filter((a: any) => a.statut === 'partiel');
    const vals = new Set<number>();
    source.forEach((a: any) => vals.add(a.nb_manquees));
    return Array.from(vals).sort((a, b) => a - b);
  }, [data]);

  const nbPasseesOptions = useMemo(() => {
    if (!data) return [];
    const source = (data as any).absents_partiels || data.absents_et_partiels.filter((a: any) => a.statut === 'partiel');
    const vals = new Set<number>();
    source.forEach((a: any) => vals.add(a.nb_passees));
    return Array.from(vals).sort((a, b) => a - b);
  }, [data]);

  const handlePrint = () => { if (!data) return; const w = window.open('', '_blank'); if (!w) return; w.document.write(buildPrintHTML(data, printFont, includeAbsentsInBilan)); w.document.close(); setTimeout(() => w.print(), 400); };

  const handlePrintSeparateList = (type: 'absents' | 'partiels') => {
    if (!data) return;
    // Utiliser filteredList qui contient déjà TOUS les filtres appliqués
    const filtered = filteredList;
    const titre = type === 'absents' ? 'LISTE DES ABSENTS TOTAUX' : 'LISTE DES ABSENTS PARTIELS';
    const filterParts = [listFilter.niveau, listFilter.pole];
    if (listFilter.nbManquees) filterParts.push(`${listFilter.nbManquees} mat. manquée(s)`);
    if (listFilter.nbPassees) filterParts.push(`${listFilter.nbPassees} mat. composée(s)`);
    const filterLabel = filterParts.filter(Boolean).join(' — ') || 'Tous';
    const w = window.open('', '_blank');
    if (!w) return;
    const N = '#1A2E4A', G = '#C8A84E', RD = '#dc2626', OR = '#ca8a04';
    const today = new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
    const th = `padding:7px 10px;border:1px solid ${N};background:${N};color:${G};font-weight:bold;font-size:8pt;`;
    const rows = filtered.map((a: any, i: number) =>
      `<tr style="background:${i%2===0?'#fff':'#f8fafc'}">
        <td style="padding:4px 8px;border:1px solid #ddd;text-align:center;font-size:8pt">${i+1}</td>
        <td style="padding:4px 8px;border:1px solid #ddd;font-family:monospace;font-size:8pt">${a.matricule}</td>
        <td style="padding:4px 8px;border:1px solid #ddd;font-weight:bold;font-size:9pt">${a.nom} ${a.prenoms}</td>
        <td style="padding:4px 8px;border:1px solid #ddd;text-align:center">${a.niveau}</td>
        <td style="padding:4px 8px;border:1px solid #ddd">${a.classe}</td>
        <td style="padding:4px 8px;border:1px solid #ddd;font-size:8pt">${a.telephone || '—'}</td>
        ${type === 'partiels' ? `
        <td style="padding:4px 8px;border:1px solid #ddd;text-align:center;font-weight:bold;color:${OR}">${a.nb_passees}/${a.nb_prevues}</td>
        <td style="padding:4px 8px;border:1px solid #ddd;font-size:7.5pt;color:${RD}">${a.matieres_manquees.join(', ')}</td>` :
        `<td style="padding:4px 8px;border:1px solid #ddd;text-align:center">${a.nb_prevues} matière(s)</td>`}
      </tr>`).join('');

    const partielsHeaders = type === 'partiels'
      ? `<th style="${th}text-align:center">Composé</th><th style="${th}text-align:left">Matière(s) non composée(s)</th>`
      : `<th style="${th}text-align:center">Mat. prévues</th>`;

    w.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${titre}</title>
<style>@page{size:A4 landscape;margin:10mm}*{margin:0;padding:0;box-sizing:border-box;-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important}
body{font-family:${printFont};font-size:9pt;color:#1a1a1a}table{width:100%;border-collapse:collapse}tbody tr:nth-child(even) td{background:#f8fafc}</style></head>
<body style="padding:15px">
<table style="margin-bottom:8px"><tr>
  <td style="width:65px;vertical-align:top"><img src="/logo.png" style="width:55px;height:55px;object-fit:contain" onerror="this.style.display='none'"/></td>
  <td style="vertical-align:middle;text-align:center">
    <div style="font-size:13pt;font-weight:bold;color:${N};text-transform:uppercase">Institut International 2i</div>
    <div style="font-size:7pt;color:#666">Excellence et Innovation</div>
    <div style="border-top:2px solid ${G};margin:4px auto;width:40%"></div>
  </td><td style="width:65px"></td></tr></table>
<div style="text-align:center;margin-bottom:12px">
  <div style="font-size:12pt;font-weight:bold;color:${type === 'absents' ? RD : OR};text-transform:uppercase">${titre}</div>
  <div style="font-size:9pt;color:#555">${data.session.nom} — ${filterLabel}</div>
  <div style="font-size:9pt;color:#888;margin-top:2px">Total: <strong>${filtered.length}</strong> étudiant(s) — Édité le ${today}</div>
</div>
<table><thead><tr>
  <th style="${th}text-align:center;width:30px">N°</th>
  <th style="${th}text-align:left">Matricule</th>
  <th style="${th}text-align:left">Nom & Prénoms</th>
  <th style="${th}text-align:center">Niv.</th>
  <th style="${th}text-align:left">Classe</th>
  <th style="${th}text-align:left">Téléphone</th>
  ${partielsHeaders}
</tr></thead><tbody>${rows}</tbody></table>
<div style="margin-top:16px;text-align:center;font-size:7.5pt;color:#999;border-top:1px solid #ddd;padding-top:5px">
  Document généré le ${today} — Institut International 2i — Service Marketing</div></body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 400);
  };

  const handlePrintList = () => {
    if (!data) return;
    const label = [listFilter.niveau, listFilter.pole, detailTab === 'absents' ? 'Absents totaux' : detailTab === 'partiels' ? 'Partiels' : 'Tous'].filter(Boolean).join(' — ') || 'Tous';
    const w = window.open('', '_blank'); if (!w) return;
    w.document.write(buildAbsentsPrintHTML(data, filteredList, label, printFont)); w.document.close(); setTimeout(() => w.print(), 400);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 dark:bg-gray-900 min-h-screen">
      <div className="mb-8">
        <h1 className="text-3xl font-extrabold text-gray-900 dark:text-gray-100 flex items-center gap-3">
          <div className="p-2 rounded-xl bg-blue-100 dark:bg-blue-900/30"><BarChart3 size={28} className="text-blue-600 dark:text-blue-400" /></div>
          Participation aux Examens
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mt-2">Bilan des présences basé sur les listes brassées et les copies associées</p>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-8">
        <select value={selectedSessionId ?? ''} onChange={(e) => { setSelectedSessionId(e.target.value ? Number(e.target.value) : null); setShowList(false); }}
          className="px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm font-medium shadow-sm focus:ring-2 focus:ring-blue-500 min-w-[320px]">
          <option value="">-- Sélectionner une session --</option>
          {sessions.map(s => <option key={s.id} value={s.id}>{s.nom}</option>)}
        </select>
        <SelecteurCycle value={cycle} onChange={(c) => { setCycle(c); setShowList(false); setListFilter(f => ({ ...f, niveau: '' })); }} />
        {data && (
          <>
            <select value={printFont} onChange={e => setPrintFont(e.target.value)}
              className="px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 shadow-sm">
              {FONT_OPTIONS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
            <label className="flex items-center gap-2 px-3 py-2 text-xs text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition shadow-sm">
              <input type="checkbox" checked={includeAbsentsInBilan} onChange={e => setIncludeAbsentsInBilan(e.target.checked)} className="rounded accent-blue-600" />
              Inclure absents
            </label>
            <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition shadow-sm"><Printer size={16} /> Imprimer le bilan</button>
          </>
        )}
      </div>

      {isLoading || isFetching ? <div className="flex justify-center py-24"><Spinner /></div>
      : !data ? <div className="text-center py-24 text-gray-400"><GraduationCap size={56} className="mx-auto mb-4 opacity-20" /><p className="text-lg">Sélectionnez une session</p></div>
      : (
        <div className="space-y-8">
          {/* ── Cards globales ── */}
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
            {[
              { icon: <Users size={20} />, label: 'Inscrits', value: data.global.total_inscrits, accent: 'bg-blue-50 dark:bg-blue-900/15 border-blue-200 dark:border-blue-800', vColor: 'text-blue-700 dark:text-blue-300' },
              { icon: <UserCheck size={20} />, label: 'Présents', value: data.global.total_presents, accent: 'bg-green-50 dark:bg-green-900/15 border-green-200 dark:border-green-800', vColor: 'text-green-700 dark:text-green-300' },
              { icon: <CheckCircle2 size={20} />, label: 'Complets', value: data.global.total_complets, accent: 'bg-emerald-50 dark:bg-emerald-900/15 border-emerald-200 dark:border-emerald-800', vColor: 'text-emerald-700 dark:text-emerald-300' },
              { icon: <AlertTriangle size={20} />, label: 'Partiels', value: data.global.total_partiels, accent: 'bg-orange-50 dark:bg-orange-900/15 border-orange-200 dark:border-orange-800', vColor: 'text-orange-700 dark:text-orange-300' },
              { icon: <UserX size={20} />, label: 'Absents', value: data.global.total_absents, accent: 'bg-red-50 dark:bg-red-900/15 border-red-200 dark:border-red-800', vColor: 'text-red-700 dark:text-red-300' },
            ].map(s => (
              <div key={s.label} className={`rounded-xl border p-4 ${s.accent}`}>
                <div className="text-gray-400 mb-1">{s.icon}</div>
                <div className={`text-2xl font-extrabold ${s.vColor}`}>{s.value}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{s.label}</div>
              </div>
            ))}
            <div className="rounded-xl border p-4 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 flex items-center justify-center">
              <TauxCircle taux={data.global.taux_participation} size={80} />
            </div>
          </div>

          {/* ── Par Pôle ── */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-bold text-gray-800 dark:text-gray-200 flex items-center gap-2 mb-5"><Building2 size={20} /> Bilan par Pôle</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {Object.entries(data.par_pole).map(([pole, d]: [string, any]) => {
                const c = getPC(pole);
                return (
                  <div key={pole} className={`rounded-xl border p-5 ${c.light}`}>
                    <div className="flex items-center justify-between mb-4">
                      <span className={`font-bold ${c.text}`}>{pole}</span>
                      <TauxCircle taux={d.taux} size={60} />
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="text-gray-500">Inscrits</div><div className="font-bold text-right">{d.inscrits}</div>
                      <div className="text-green-600">Présents</div><div className="font-bold text-right text-green-600">{d.presents}</div>
                      <div className="text-orange-500">Partiels</div><div className="font-bold text-right text-orange-500">{d.partiels}</div>
                      <div className="text-red-500">Absents</div><div className="font-bold text-right text-red-500">{d.absents}</div>
                    </div>
                    <div className="mt-3 h-2.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${c.bar} transition-all duration-700`} style={{ width: `${d.taux}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Par Niveau ── */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-bold text-gray-800 dark:text-gray-200 flex items-center gap-2 mb-5"><GraduationCap size={20} /> Bilan par Niveau</h2>
            <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
              <table className="w-full text-sm">
                <thead><tr className="bg-gray-900 dark:bg-gray-700 text-white text-xs uppercase">
                  <th className="px-4 py-3 text-left">Niveau</th><th className="px-4 py-3 text-center">Inscrits</th><th className="px-4 py-3 text-center">Présents</th>
                  <th className="px-4 py-3 text-center">Complets</th><th className="px-4 py-3 text-center">Partiels</th><th className="px-4 py-3 text-center">Absents</th><th className="px-4 py-3 text-center">Taux</th><th className="px-4 py-3 w-32">Graphe</th>
                </tr></thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {Object.entries(data.par_niveau).map(([niv, d]: [string, any], i) => (
                    <tr key={niv} className={i%2 ? 'bg-gray-50 dark:bg-gray-800/60' : ''}>
                      <td className="px-4 py-3"><span className={`text-xs font-bold text-white px-2.5 py-1 rounded ${NIV_COLORS[niv] || 'bg-gray-500'}`}>{niv}</span></td>
                      <td className="px-4 py-3 text-center font-medium">{d.inscrits}</td>
                      <td className="px-4 py-3 text-center font-bold text-green-600">{d.presents}</td>
                      <td className="px-4 py-3 text-center text-emerald-600">{d.complets}</td>
                      <td className="px-4 py-3 text-center text-orange-500">{d.partiels}</td>
                      <td className="px-4 py-3 text-center text-red-500">{d.absents}</td>
                      <td className="px-4 py-3 text-center"><span className={`px-2 py-0.5 rounded-full text-xs font-bold ${d.taux>=80?'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300':d.taux>=60?'bg-yellow-100 text-yellow-800':'bg-red-100 text-red-800'}`}>{d.taux}%</span></td>
                      <td className="px-4 py-3"><div className="h-2.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden"><div className={`h-full rounded-full ${NIV_COLORS[niv]||'bg-gray-400'} transition-all duration-700`} style={{width:`${d.taux}%`}}/></div></td>
                    </tr>
                  ))}
                  <tr className="bg-gray-100 dark:bg-gray-700 font-bold">
                    <td className="px-4 py-3">TOTAL</td>
                    <td className="px-4 py-3 text-center">{data.global.total_inscrits}</td>
                    <td className="px-4 py-3 text-center text-green-600">{data.global.total_presents}</td>
                    <td className="px-4 py-3 text-center">{data.global.total_complets}</td>
                    <td className="px-4 py-3 text-center text-orange-500">{data.global.total_partiels}</td>
                    <td className="px-4 py-3 text-center text-red-500">{data.global.total_absents}</td>
                    <td className="px-4 py-3 text-center">{data.global.taux_participation}%</td>
                    <td className="px-4 py-3" />
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Bilan Niveau × Pôle ── */}
          {(data as any).par_niveau_pole && Object.keys((data as any).par_niveau_pole).length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-bold text-gray-800 dark:text-gray-200 flex items-center gap-2 mb-5">
                <TrendingUp size={20} className="text-teal-500" /> Bilan par Niveau et Pôle
              </h2>
              <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
                <table className="w-full text-sm">
                  <thead><tr className="bg-gray-900 dark:bg-gray-700 text-white text-xs uppercase">
                    <th className="px-4 py-3 text-left">Niveau</th>
                    <th className="px-4 py-3 text-left">Pôle</th>
                    <th className="px-4 py-3 text-center">Inscrits</th>
                    <th className="px-4 py-3 text-center">Complets</th>
                    <th className="px-4 py-3 text-center">Partiels</th>
                    <th className="px-4 py-3 text-center">Absents</th>
                    <th className="px-4 py-3 text-center">Taux</th>
                    <th className="px-4 py-3 w-28">Graphe</th>
                  </tr></thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {Object.entries((data as any).par_niveau_pole).map(([key, d]: [string, any], i: number) => (
                      <tr key={key} className={i%2 ? 'bg-gray-50 dark:bg-gray-800/60' : ''}>
                        <td className="px-4 py-2.5"><span className={`text-xs font-bold text-white px-2 py-0.5 rounded ${NIV_COLORS[d.niveau]||'bg-gray-500'}`}>{d.niveau}</span></td>
                        <td className="px-4 py-2.5 font-medium text-gray-700 dark:text-gray-300">{d.pole}</td>
                        <td className="px-4 py-2.5 text-center font-medium">{d.inscrits}</td>
                        <td className="px-4 py-2.5 text-center text-green-600 font-bold">{d.complets}</td>
                        <td className="px-4 py-2.5 text-center text-orange-500">{d.partiels}</td>
                        <td className="px-4 py-2.5 text-center text-red-500">{d.absents}</td>
                        <td className="px-4 py-2.5 text-center">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${d.taux>=80?'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300':d.taux>=60?'bg-yellow-100 text-yellow-800':'bg-red-100 text-red-800'}`}>{d.taux}%</span>
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="h-2.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full transition-all duration-700 ${d.taux>=80?'bg-green-500':d.taux>=60?'bg-yellow-400':'bg-red-500'}`} style={{width:`${d.taux}%`}}/>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── Bilan par Classe ── */}
          {(data as any).par_classe && Object.keys((data as any).par_classe).length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-bold text-gray-800 dark:text-gray-200 flex items-center gap-2 mb-5">
                <Users size={20} className="text-indigo-500" /> Bilan par Classe
                <span className="text-xs font-normal text-gray-500">({Object.keys((data as any).par_classe).length} classes)</span>
              </h2>
              <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
                <table className="w-full text-sm">
                  <thead><tr className="bg-gray-900 dark:bg-gray-700 text-white text-xs uppercase">
                    <th className="px-4 py-3 text-left">Classe</th>
                    <th className="px-4 py-3 text-left">Option</th>
                    <th className="px-4 py-3 text-left">Pôle</th>
                    <th className="px-4 py-3 text-center">Mat.</th>
                    <th className="px-4 py-3 text-center">Inscrits</th>
                    <th className="px-4 py-3 text-center">Complets</th>
                    <th className="px-4 py-3 text-center">Partiels</th>
                    <th className="px-4 py-3 text-center">Absents</th>
                    <th className="px-4 py-3 text-center">Taux</th>
                    <th className="px-4 py-3 w-24">Graphe</th>
                  </tr></thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {Object.entries((data as any).par_classe).map(([cls, d]: [string, any], i) => {
                      const noMatieres = d.nb_matieres === 0;
                      return (
                      <tr key={cls} className={`${i%2 ? 'bg-gray-50 dark:bg-gray-800/60' : ''} ${noMatieres ? 'bg-red-50/50 dark:bg-red-900/10' : ''}`}>
                        <td className="px-4 py-2.5 font-bold text-xs text-gray-800 dark:text-gray-200">
                          {cls}
                          {noMatieres && <span className="ml-1 text-[9px] text-red-500">⚠</span>}
                        </td>
                        <td className="px-4 py-2.5 text-xs text-gray-600 dark:text-gray-400">{d.option || '—'}</td>
                        <td className="px-4 py-2.5 text-xs text-gray-600 dark:text-gray-400">{d.pole || '—'}</td>
                        <td className={`px-4 py-2.5 text-center text-xs font-bold ${noMatieres ? 'text-red-500' : 'text-blue-600'}`}>{d.nb_matieres || 0}</td>
                        <td className="px-4 py-2.5 text-center font-medium">{d.inscrits}</td>
                        <td className="px-4 py-2.5 text-center text-green-600 font-bold">{d.complets}</td>
                        <td className="px-4 py-2.5 text-center text-orange-500">{d.partiels}</td>
                        <td className="px-4 py-2.5 text-center text-red-500">{d.absents}</td>
                        <td className="px-4 py-2.5 text-center">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${d.taux>=80?'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300':d.taux>=60?'bg-yellow-100 text-yellow-800':'bg-red-100 text-red-800'}`}>{d.taux}%</span>
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full transition-all duration-700 ${d.taux>=80?'bg-green-500':d.taux>=60?'bg-yellow-400':'bg-red-500'}`} style={{width:`${d.taux}%`}}/>
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── Suggestions intelligentes ── */}
          {(data as any).suggestions && (data as any).suggestions.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-bold text-gray-800 dark:text-gray-200 flex items-center gap-2 mb-4">
                <AlertTriangle size={20} className="text-amber-500" /> Suggestions & Alertes
              </h2>
              <div className="space-y-3">
                {(data as any).suggestions.map((s: any, i: number) => (
                  <div key={i} className={`rounded-lg border p-4 ${
                    s.type === 'critique' ? 'bg-red-50 dark:bg-red-900/10 border-red-300 dark:border-red-700' :
                    s.type === 'alerte' ? 'bg-amber-50 dark:bg-amber-900/10 border-amber-300 dark:border-amber-700' :
                    s.type === 'attention' ? 'bg-orange-50 dark:bg-orange-900/10 border-orange-300 dark:border-orange-700' :
                    'bg-blue-50 dark:bg-blue-900/10 border-blue-300 dark:border-blue-700'
                  }`}>
                    <h4 className={`text-sm font-bold ${
                      s.type === 'critique' ? 'text-red-800 dark:text-red-200' :
                      s.type === 'alerte' ? 'text-amber-800 dark:text-amber-200' :
                      s.type === 'attention' ? 'text-orange-800 dark:text-orange-200' :
                      'text-blue-800 dark:text-blue-200'
                    }`}>{s.titre}</h4>
                    <pre className="text-xs text-gray-600 dark:text-gray-400 mt-1 whitespace-pre-wrap font-sans">{s.detail}</pre>
                    <div className="text-xs text-gray-500 mt-2 bg-white/50 dark:bg-gray-900/30 rounded p-2 border border-gray-200 dark:border-gray-700">
                      <span className="font-semibold text-gray-700 dark:text-gray-300">Action recommandée :</span>
                      <pre className="mt-1 whitespace-pre-wrap font-sans">{s.action}</pre>
                      {s.links && (s.links as any[]).length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                          {(s.links as any[]).map((link: any, li: number) => (
                            <button
                              key={li}
                              onClick={() => {
                                if (link.url === '#absents') {
                                  setDetailTab('absents');
                                  setShowList(true);
                                } else if (link.url === '#partiels') {
                                  setDetailTab('partiels');
                                  setShowList(true);
                                } else {
                                  navigate(link.url);
                                }
                              }}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 shadow-sm transition-colors"
                            >
                              <ExternalLink size={12} />
                              {link.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Analyse par matière ── */}
          {(data as any).analyse_matieres && (data as any).analyse_matieres.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-bold text-gray-800 dark:text-gray-200 flex items-center gap-2 mb-4">
                <BarChart3 size={20} className="text-purple-500" /> Analyse par Matière
                <span className="text-xs font-normal text-gray-500">({(data as any).analyse_matieres.length} matières)</span>
              </h2>
              <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
                <div className="max-h-[400px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-gray-900 dark:bg-gray-700 text-white text-xs uppercase">
                      <tr>
                        <th className="px-4 py-3 text-left">Matière</th>
                        <th className="px-4 py-3 text-center">Inscrits</th>
                        <th className="px-4 py-3 text-center">Présents</th>
                        <th className="px-4 py-3 text-center">Absents</th>
                        <th className="px-4 py-3 text-center">Taux absence</th>
                        <th className="px-4 py-3 w-32">Graphe</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {(data as any).analyse_matieres.map((m: any, i: number) => (
                        <tr key={m.matiere} className={`${i%2?'bg-gray-50 dark:bg-gray-800/60':''} ${m.taux_absence === 100 ? 'bg-red-50/50 dark:bg-red-900/10' : ''}`}>
                          <td className="px-4 py-2">
                            <div className="font-medium text-gray-800 dark:text-gray-200 text-xs">{m.matiere}</div>
                            {m.formateurs && m.formateurs.length > 0 && (
                              <div className="text-[9px] text-blue-600 dark:text-blue-400 mt-0.5">
                                {m.formateurs.join(', ')}
                              </div>
                            )}
                            {m.classes_absentes && m.classes_absentes.length > 0 && m.taux_absence > 30 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {m.classes_absentes.slice(0, 5).map((c: any) => (
                                  <span key={c.classe} className="text-[9px] px-1 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded">
                                    {c.classe} ({c.nb})
                                  </span>
                                ))}
                                {m.poles_absents && m.poles_absents.length > 0 && (
                                  <span className="text-[9px] px-1 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded">
                                    {m.poles_absents.map((p: any) => p.pole).join(', ')}
                                  </span>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-2 text-center text-xs">{m.nb_inscrits}</td>
                          <td className="px-4 py-2 text-center text-xs text-green-600 font-bold">{m.nb_presents}</td>
                          <td className="px-4 py-2 text-center text-xs text-red-600 font-bold">{m.nb_absents}</td>
                          <td className="px-4 py-2 text-center">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                              m.taux_absence > 30 ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' :
                              m.taux_absence > 15 ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300' :
                              'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                            }`}>{m.taux_absence}%</span>
                          </td>
                          <td className="px-4 py-2">
                            <div className="h-2.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full transition-all duration-700 ${m.taux_absence > 30 ? 'bg-red-500' : m.taux_absence > 15 ? 'bg-orange-400' : 'bg-green-500'}`}
                                style={{ width: `${100 - m.taux_absence}%` }} />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ── Synthèse matières manquées ── */}
          {(() => {
            const partielsData = (data as any).absents_partiels || data.absents_et_partiels.filter((a: any) => a.statut === 'partiel');
            if (!partielsData || partielsData.length === 0) return null;

            // Répartition globale par nb matières manquées
            const repartition: Record<number, number> = {};
            partielsData.forEach((a: any) => { repartition[a.nb_manquees] = (repartition[a.nb_manquees] || 0) + 1; });
            const repartitionList = Object.entries(repartition).map(([k, v]) => ({ nb: Number(k), count: v as number })).sort((a, b) => a.nb - b.nb);

            // Par niveau
            const parNiveau: Record<string, Record<number, number>> = {};
            partielsData.forEach((a: any) => {
              if (!parNiveau[a.niveau]) parNiveau[a.niveau] = {};
              parNiveau[a.niveau][a.nb_manquees] = (parNiveau[a.niveau][a.nb_manquees] || 0) + 1;
            });

            // Par pôle
            const parPole: Record<string, Record<number, number>> = {};
            partielsData.forEach((a: any) => {
              if (!parPole[a.pole]) parPole[a.pole] = {};
              parPole[a.pole][a.nb_manquees] = (parPole[a.pole][a.nb_manquees] || 0) + 1;
            });

            const allNbs = repartitionList.map(r => r.nb);

            const handlePrintSynthese = () => {
              const N = '#1A2E4A', G = '#C8A84E';
              const today = new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
              const th = `padding:7px 10px;border:1px solid ${N};background:${N};color:${G};font-weight:bold;font-size:8pt;`;
              const td = `padding:6px 10px;border:1px solid #ddd;text-align:center;font-size:9pt;`;

              const globalRows = repartitionList.map(r =>
                `<tr><td style="${td}font-weight:bold">${r.nb} matière${r.nb > 1 ? 's' : ''}</td><td style="${td}font-weight:bold;color:#dc2626;font-size:12pt">${r.count}</td><td style="${td}">${Math.round(r.count / partielsData.length * 100)}%</td></tr>`
              ).join('');

              const niveauRows = Object.entries(parNiveau).sort().map(([niv, counts]) => {
                const cells = allNbs.map(nb => `<td style="${td}">${(counts as any)[nb] || 0}</td>`).join('');
                const total = Object.values(counts).reduce((s: number, v: any) => s + v, 0);
                return `<tr><td style="${td}font-weight:bold">${niv}</td>${cells}<td style="${td}font-weight:bold">${total}</td></tr>`;
              }).join('');

              const poleRows = Object.entries(parPole).sort().map(([pole, counts]) => {
                const cells = allNbs.map(nb => `<td style="${td}">${(counts as any)[nb] || 0}</td>`).join('');
                const total = Object.values(counts).reduce((s: number, v: any) => s + v, 0);
                return `<tr><td style="${td}font-weight:bold">${pole}</td>${cells}<td style="${td}font-weight:bold">${total}</td></tr>`;
              }).join('');

              const nbHeaders = allNbs.map(nb => `<th style="${th}">${nb} mat.</th>`).join('');

              const w = window.open('', '_blank');
              if (!w) return;
              w.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Synthèse matières manquées</title>
<style>@page{size:A4;margin:12mm}*{margin:0;padding:0;box-sizing:border-box;-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important}
body{font-family:'Segoe UI',Arial,sans-serif;font-size:10pt;color:#1a1a1a}table{width:100%;border-collapse:collapse;margin-bottom:16px}
tbody tr:nth-child(even) td{background:#f8fafc}</style></head>
<body style="padding:20px">
<table style="margin-bottom:10px"><tr>
  <td style="width:65px;vertical-align:top"><img src="/logo.png" style="width:55px;height:55px;object-fit:contain" onerror="this.style.display='none'"/></td>
  <td style="vertical-align:middle;text-align:center">
    <div style="font-size:14pt;font-weight:bold;color:${N};text-transform:uppercase">Institut International 2i</div>
    <div style="font-size:7pt;color:#666">Excellence et Innovation</div>
    <div style="border-top:2px solid ${G};margin:4px auto;width:40%"></div>
  </td><td style="width:65px"></td></tr></table>
<div style="text-align:center;margin-bottom:16px">
  <div style="font-size:13pt;font-weight:bold;color:${N};text-transform:uppercase">Synthèse des Matières Manquées</div>
  <div style="font-size:9pt;color:#555">${data.session.nom}</div>
  <div style="font-size:9pt;color:#888">Total étudiants partiels : ${partielsData.length} — Édité le ${today}</div>
</div>

<div style="font-weight:bold;font-size:11pt;color:${N};margin:12px 0 6px;border-bottom:2px solid ${G};padding-bottom:3px">Répartition Globale</div>
<table><thead><tr><th style="${th}text-align:left">Nb matières manquées</th><th style="${th}">Nb étudiants</th><th style="${th}">% du total partiels</th></tr></thead>
<tbody>${globalRows}
<tr style="background:#fef3c7;font-weight:bold"><td style="${td}">TOTAL</td><td style="${td}font-size:12pt">${partielsData.length}</td><td style="${td}">100%</td></tr>
</tbody></table>

<div style="font-weight:bold;font-size:11pt;color:${N};margin:12px 0 6px;border-bottom:2px solid ${G};padding-bottom:3px">Par Niveau</div>
<table><thead><tr><th style="${th}text-align:left">Niveau</th>${nbHeaders}<th style="${th}">Total</th></tr></thead>
<tbody>${niveauRows}</tbody></table>

<div style="font-weight:bold;font-size:11pt;color:${N};margin:12px 0 6px;border-bottom:2px solid ${G};padding-bottom:3px">Par Pôle</div>
<table><thead><tr><th style="${th}text-align:left">Pôle</th>${nbHeaders}<th style="${th}">Total</th></tr></thead>
<tbody>${poleRows}</tbody></table>

<div style="margin-top:20px;text-align:center;font-size:8pt;color:#999;border-top:1px solid #ddd;padding-top:6px">
  Document généré le ${today} — Système de Gestion des Notes — Institut International 2i</div>
<script>setTimeout(()=>window.print(), 300);</script>
</body></html>`);
              w.document.close();
            };

            return (
              <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="text-lg font-bold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                    <BarChart3 size={20} className="text-orange-500" /> Synthèse des matières manquées
                    <span className="text-xs font-normal text-gray-500">({partielsData.length} étudiants partiels)</span>
                  </h2>
                  <button onClick={handlePrintSynthese} className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 border border-orange-200 dark:border-orange-800 rounded-lg hover:bg-orange-100 transition">
                    <Printer size={14} /> Imprimer synthèse
                  </button>
                </div>

                {/* Répartition globale */}
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Répartition globale</h3>
                  <div className="flex flex-wrap gap-3">
                    {repartitionList.map(r => (
                      <button
                        key={r.nb}
                        onClick={() => { setDetailTab('partiels'); setListFilter(f => ({ ...f, nbManquees: String(r.nb), nbPassees: '' })); setShowList(true); }}
                        className="flex flex-col items-center px-4 py-3 rounded-xl border-2 border-orange-200 dark:border-orange-800 bg-orange-50/50 dark:bg-orange-900/10 hover:bg-orange-100 dark:hover:bg-orange-900/20 transition cursor-pointer min-w-[80px]"
                      >
                        <span className="text-2xl font-extrabold text-orange-600">{r.count}</span>
                        <span className="text-[10px] text-orange-800 dark:text-orange-300 font-medium mt-0.5">
                          {r.nb} mat. manquée{r.nb > 1 ? 's' : ''}
                        </span>
                        <span className="text-[9px] text-gray-400">{Math.round(r.count / partielsData.length * 100)}%</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Tableau croisé par Niveau */}
                <div className="mb-4">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Par Niveau</h3>
                  <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                    <table className="w-full text-sm">
                      <thead><tr className="bg-gray-900 dark:bg-gray-700 text-white text-[10px] uppercase">
                        <th className="px-3 py-2 text-left">Niveau</th>
                        {allNbs.map(nb => <th key={nb} className="px-3 py-2 text-center">{nb} mat.</th>)}
                        <th className="px-3 py-2 text-center">Total</th>
                      </tr></thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                        {Object.entries(parNiveau).sort().map(([niv, counts], i) => {
                          const total = Object.values(counts).reduce((s: number, v) => s + (v as number), 0);
                          return (
                            <tr key={niv} className={i%2?'bg-gray-50 dark:bg-gray-800/60':''}>
                              <td className="px-3 py-2 font-bold">
                                <span className={`text-xs font-bold text-white px-2 py-0.5 rounded ${NIV_COLORS[niv]||'bg-gray-500'}`}>{niv}</span>
                              </td>
                              {allNbs.map(nb => <td key={nb} className="px-3 py-2 text-center text-xs">{(counts as any)[nb] || <span className="text-gray-300">—</span>}</td>)}
                              <td className="px-3 py-2 text-center font-bold text-orange-600">{total}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Tableau croisé par Pôle */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Par Pôle</h3>
                  <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                    <table className="w-full text-sm">
                      <thead><tr className="bg-gray-900 dark:bg-gray-700 text-white text-[10px] uppercase">
                        <th className="px-3 py-2 text-left">Pôle</th>
                        {allNbs.map(nb => <th key={nb} className="px-3 py-2 text-center">{nb} mat.</th>)}
                        <th className="px-3 py-2 text-center">Total</th>
                      </tr></thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                        {Object.entries(parPole).sort().map(([pole, counts], i) => {
                          const total = Object.values(counts).reduce((s: number, v) => s + (v as number), 0);
                          return (
                            <tr key={pole} className={i%2?'bg-gray-50 dark:bg-gray-800/60':''}>
                              <td className="px-3 py-2 font-medium text-gray-700 dark:text-gray-300">{pole}</td>
                              {allNbs.map(nb => <td key={nb} className="px-3 py-2 text-center text-xs">{(counts as any)[nb] || <span className="text-gray-300">—</span>}</td>)}
                              <td className="px-3 py-2 text-center font-bold text-orange-600">{total}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* ── Boutons impression rapide ── */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={() => { setDetailTab('absents'); setListFilter(f => ({ ...f, statut: 'absent', nbManquees: '', nbPassees: '' })); setShowList(true); }}
              className="flex items-center justify-between px-5 py-4 bg-red-50 dark:bg-red-900/15 border-2 border-red-200 dark:border-red-800 rounded-xl hover:bg-red-100 dark:hover:bg-red-900/25 transition group"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-red-600 text-white"><UserX size={20} /></div>
                <div className="text-left">
                  <div className="font-bold text-red-800 dark:text-red-200">Absents totaux</div>
                  <div className="text-xs text-red-600 dark:text-red-300">Étudiants n'ayant composé aucune matière</div>
                </div>
              </div>
              <span className="text-2xl font-extrabold text-red-700 dark:text-red-300 group-hover:scale-110 transition">{data.global.total_absents}</span>
            </button>
            <button
              onClick={() => { setListFilter(f => ({ ...f, statut: 'partiel' })); setShowList(true); }}
              className="flex items-center justify-between px-5 py-4 bg-orange-50 dark:bg-orange-900/15 border-2 border-orange-200 dark:border-orange-800 rounded-xl hover:bg-orange-100 dark:hover:bg-orange-900/25 transition group"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-orange-600 text-white"><AlertTriangle size={20} /></div>
                <div className="text-left">
                  <div className="font-bold text-orange-800 dark:text-orange-200">Absents partiels</div>
                  <div className="text-xs text-orange-600 dark:text-orange-300">Étudiants ayant raté certaines matières</div>
                </div>
              </div>
              <span className="text-2xl font-extrabold text-orange-700 dark:text-orange-300 group-hover:scale-110 transition">{data.global.total_partiels}</span>
            </button>
          </div>

          {/* ── Listes détaillées avec onglets ── */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <button onClick={() => setShowList(!showList)} className="w-full flex items-center justify-between px-6 py-5 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30"><UserX size={20} className="text-red-500" /></div>
                <div className="text-left">
                  <div className="font-bold text-gray-800 dark:text-gray-200">Listes détaillées par catégorie</div>
                  <div className="text-sm text-gray-500">{data.global.total_absents} absent(s) total · {data.global.total_partiels} partiel(s) · {data.global.total_complets} complet(s)</div>
                </div>
              </div>
              {showList ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>

            {showList && (
              <div className="border-t border-gray-200 dark:border-gray-700">
                {/* Sous-onglets — ajoute « Vrais absents » si session rattrapage */}
                <div className="flex border-b border-gray-200 dark:border-gray-700">
                  {([
                    { key: 'absents' as DetailTab, label: 'Absents totaux', count: data.global.total_absents, color: 'red' },
                    { key: 'partiels' as DetailTab, label: 'Absents partiels', count: data.global.total_partiels, color: 'orange' },
                    ...((data as any).session?.type_session === 'rattrapage' && Array.isArray((data as any).vrais_absents)
                      ? [{ key: 'vrais_absents' as DetailTab, label: 'Vrais absents', count: (data as any).vrais_absents.length, color: 'purple' as const }]
                      : []),
                  ] as const).map(tab => (
                    <button
                      key={tab.key}
                      onClick={() => setDetailTab(tab.key)}
                      title={tab.key === 'vrais_absents'
                        ? "Étudiants absents en session ordinaire ET en rattrapage — vrais absents du semestre"
                        : undefined}
                      className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-all border-b-2 ${
                        detailTab === tab.key
                          ? tab.color === 'red'
                            ? 'border-red-500 text-red-700 dark:text-red-300 bg-red-50/50 dark:bg-red-900/10'
                            : tab.color === 'orange'
                              ? 'border-orange-500 text-orange-700 dark:text-orange-300 bg-orange-50/50 dark:bg-orange-900/10'
                              : 'border-purple-500 text-purple-700 dark:text-purple-300 bg-purple-50/50 dark:bg-purple-900/10'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/30'
                      }`}
                    >
                      {tab.label}
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                        tab.color === 'red'
                          ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
                          : tab.color === 'orange'
                            ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300'
                            : 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300'
                      }`}>{tab.count}</span>
                    </button>
                  ))}
                </div>

                {/* Filtres */}
                <div className="flex flex-wrap items-center gap-3 px-6 py-3 bg-gray-50 dark:bg-gray-700/30">
                  <div className="relative flex-1 min-w-[200px]">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input type="text" value={listFilter.search} onChange={e => setListFilter(f => ({...f, search: e.target.value}))}
                      placeholder="Rechercher nom, matricule..." className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200" />
                  </div>
                  <select value={listFilter.niveau} onChange={e => setListFilter(f => ({...f, niveau: e.target.value}))} className="px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700">
                    <option value="">Tous niveaux</option>
                    {Object.keys(data.par_niveau).map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                  <select value={listFilter.pole} onChange={e => setListFilter(f => ({...f, pole: e.target.value}))} className="px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700">
                    <option value="">Tous pôles</option>
                    {Object.keys(data.par_pole).map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                  {detailTab === 'partiels' && (
                    <>
                      <select value={listFilter.nbManquees} onChange={e => setListFilter(f => ({...f, nbManquees: e.target.value}))}
                        className="px-3 py-2 text-sm border border-red-200 dark:border-red-700 rounded-lg bg-white dark:bg-gray-700 text-red-700 dark:text-red-300"
                        title="Filtrer par nombre de matières NON composées">
                        <option value="">Mat. manquées</option>
                        {nbManqueesOptions.map(n => <option key={n} value={n}>{n} manquée{n > 1 ? 's' : ''}</option>)}
                      </select>
                      <select value={listFilter.nbPassees} onChange={e => setListFilter(f => ({...f, nbPassees: e.target.value}))}
                        className="px-3 py-2 text-sm border border-green-200 dark:border-green-700 rounded-lg bg-white dark:bg-gray-700 text-green-700 dark:text-green-300"
                        title="Filtrer par nombre de matières composées">
                        <option value="">Mat. composées</option>
                        {nbPasseesOptions.map(n => <option key={n} value={n}>{n} composée{n > 1 ? 's' : ''}</option>)}
                      </select>
                    </>
                  )}
                  <button onClick={() => handlePrintSeparateList(detailTab as any)} className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-100 transition">
                    <Printer size={14} /> Imprimer ({filteredList.length})
                  </button>
                </div>

                <div className="max-h-[500px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-gray-100 dark:bg-gray-700 text-xs text-gray-500 dark:text-gray-400 uppercase">
                      <tr>
                        <th className="px-4 py-2.5 text-left w-10">N°</th>
                        <th className="px-4 py-2.5 text-left">Matricule</th>
                        <th className="px-4 py-2.5 text-left">Nom & Prénoms</th>
                        <th className="px-4 py-2.5 text-center">Niv.</th>
                        <th className="px-4 py-2.5 text-left">Classe</th>
                        <th className="px-4 py-2.5 text-left">Téléphone</th>
                        <th className="px-4 py-2.5 text-center">Total mat.</th>
                        <th className="px-4 py-2.5 text-center">Composé</th>
                        <th className="px-4 py-2.5 text-center">Manqué</th>
                        <th className="px-4 py-2.5 text-left">{detailTab === 'partiels' ? 'Matière(s) non composée(s)' : 'Détail'}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {filteredList.map((a: any, i: number) => (
                        <tr key={`${a.etudiant_id}-${i}`} className={i%2?'bg-gray-50 dark:bg-gray-800/50':''}>
                          <td className="px-4 py-2 text-gray-400 text-xs">{i+1}</td>
                          <td className="px-4 py-2 font-mono text-xs">{a.matricule}</td>
                          <td className="px-4 py-2">
                            <div className="font-medium text-gray-800 dark:text-gray-200 text-sm">{a.nom} {a.prenoms}</div>
                            <div className="text-xs text-gray-400">{a.option}</div>
                          </td>
                          <td className="px-4 py-2 text-center"><span className={`text-xs font-bold text-white px-2 py-0.5 rounded ${NIV_COLORS[a.niveau]||'bg-gray-500'}`}>{a.niveau}</span></td>
                          <td className="px-4 py-2 text-xs text-gray-500">{a.classe}</td>
                          <td className="px-4 py-2 text-xs text-gray-500">{a.telephone || '—'}</td>
                          <td className="px-4 py-2 text-center text-xs font-bold text-blue-600">{a.nb_prevues}</td>
                          <td className="px-4 py-2 text-center text-xs font-bold text-green-600">{a.nb_passees}</td>
                          <td className="px-4 py-2 text-center text-xs font-bold text-red-600">{a.nb_manquees}</td>
                          <td className="px-4 py-2">
                            {a.statut === 'absent' ? (
                              <span className="text-xs text-red-500 italic font-medium">Aucune matière composée</span>
                            ) : a.matieres_manquees && a.matieres_manquees.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {a.matieres_manquees.map((m: string, j: number) => (
                                  <span key={j} className="inline-block px-1.5 py-0.5 text-[10px] bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800 rounded">
                                    {m}
                                  </span>
                                ))}
                              </div>
                            ) : <span className="text-xs text-green-600 font-medium">Toutes composées</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredList.length === 0 && <p className="text-center py-10 text-gray-400">Aucun résultat.</p>}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StatistiquesParticipation;
